import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft, Plus, Send } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { CommunicationsPageShell } from "../shared/CommunicationsPageShell";
import { useCommunicationDetail } from "../catalog/use-communications";
import { useCommunicationLocales } from "../locales/use-communication-locales";
import {
  activeChannels,
  addableChannelTypes,
  sortChannels,
} from "../shared/communication-selectors";
import { AudienceBadge } from "../shared/AudienceBadge";
import { ConfigSection } from "./ConfigSection";
import { ChannelCard } from "./ChannelCard";
import { AddChannelDialog } from "./AddChannelDialog";
import { TestDispatchDialog } from "./TestDispatchDialog";

export function CommunicationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const { communication, isLoading, isError } = useCommunicationDetail(id);
  const localesQuery = useCommunicationLocales();
  const [addChannelOpen, setAddChannelOpen] = useState(false);
  const [testDispatchOpen, setTestDispatchOpen] = useState(false);

  return (
    <CommunicationsPageShell
      screenTestId="communication-detail-screen"
      titleKey="communications.detail.title"
      subtitleKey="communications.detail.subtitle"
    >
      <div>
        <Link
          to="/communications"
          data-testid="communication-back-link"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="mr-1 h-4 w-4" aria-hidden="true" />
          {t("communications.detail.back")}
        </Link>
      </div>

      {isLoading && (
        <p data-testid="communication-detail-loading" className="text-sm text-muted-foreground">
          {t("communications.detail.loading")}
        </p>
      )}

      {isError && (
        <p data-testid="communication-detail-error" className="text-sm text-destructive">
          {t("communications.detail.loadError")}
        </p>
      )}

      {!isLoading && !isError && !communication && (
        <p data-testid="communication-not-found" className="text-sm text-destructive">
          {t("communications.detail.notFound")}
        </p>
      )}

      {communication && (
        <>
          <section className="space-y-2 rounded-md border border-border bg-card p-4">
            <div className="flex flex-wrap items-center gap-2">
              <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-sm">
                {communication.key}
              </code>
              <AudienceBadge audience={communication.audience} />
              {communication.isCritical && (
                <span
                  data-testid="communication-detail-critical"
                  className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 ring-1 ring-inset ring-red-300"
                >
                  {t("communications.flags.critical")}
                </span>
              )}
            </div>
            {communication.description && (
              <p className="text-sm text-muted-foreground">{communication.description}</p>
            )}
          </section>

          <ConfigSection communication={communication} />

          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {t("communications.detail.channelsHeading")}
              </h3>
              <div className="flex items-center gap-2">
                {activeChannels(communication).length > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="communication-test-dispatch-button"
                    onClick={() => setTestDispatchOpen(true)}
                  >
                    <Send className="mr-2 h-4 w-4" aria-hidden="true" />
                    {t("communications.testDispatch.button")}
                  </Button>
                )}
                {addableChannelTypes(communication).length > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="communication-add-channel-button"
                    onClick={() => setAddChannelOpen(true)}
                  >
                    <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                    {t("communications.channel.add.button")}
                  </Button>
                )}
              </div>
            </div>
            {communication.channels.length === 0 ? (
              <p
                data-testid="communication-detail-no-channels"
                className="text-sm text-muted-foreground"
              >
                {t("communications.detail.noChannels")}
              </p>
            ) : (
              sortChannels(communication.channels).map((channel) => (
                <ChannelCard
                  key={channel.id}
                  communicationId={communication.id}
                  channel={channel}
                  locales={localesQuery.data ?? []}
                  isCritical={communication.isCritical}
                  audience={communication.audience}
                />
              ))
            )}
          </section>

          <AddChannelDialog
            open={addChannelOpen}
            onOpenChange={setAddChannelOpen}
            communication={communication}
          />

          <TestDispatchDialog
            open={testDispatchOpen}
            onOpenChange={setTestDispatchOpen}
            communication={communication}
          />
        </>
      )}
    </CommunicationsPageShell>
  );
}
